import { useEffect, useRef } from "react"
import { useReducedMotion } from "motion/react"
import { blend, toSvgPath, type Points } from "../brain/contour"
import type { BracketTone } from "./viewfinder-brackets"

// Their own car, outlined on the live picture: proof the camera sees the
// right thing. White while it's being lined up, green when the camera has
// locked on. The outline glides from one trace to the next (160 ms, the
// path is set directly, React doesn't re-render per frame); reduced motion
// jumps.

const GLIDE_MS = 160

export function CarHighlight({
  points,
  width,
  height,
  tone,
}: {
  // 0–1 of the visible frame.
  points: Points
  width: number
  height: number
  tone: BracketTone
}) {
  const reduce = useReducedMotion()
  const line = useRef<SVGPathElement>(null)
  const halo = useRef<SVGPathElement>(null)
  const shown = useRef<Points | null>(null)

  useEffect(() => {
    const draw = (pts: Points) => {
      const d = toSvgPath(pts, (x, y) => [x * width, y * height])
      line.current?.setAttribute("d", d)
      halo.current?.setAttribute("d", d)
      shown.current = pts
    }
    const from = shown.current
    if (!from || from.length !== points.length || reduce) {
      draw(points)
      return
    }
    let raf = 0
    const start = performance.now()
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / GLIDE_MS)
      draw(blend(from, points, 1 - (1 - k) ** 3))
      if (k < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [points, width, height, reduce])

  const locked = tone === "locked"
  return (
    <svg
      width={width}
      height={height}
      className="pointer-events-none absolute inset-0"
      aria-hidden
      data-testid="car-highlight"
      data-tone={tone}
    >
      <path ref={halo} fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth={6} strokeLinejoin="round" />
      <path
        ref={line}
        strokeWidth={2.5}
        strokeLinejoin="round"
        style={{
          fill: locked ? "rgb(52 211 153 / 0.16)" : "rgb(255 255 255 / 0.12)",
          stroke: locked ? "var(--color-signal-on-dark)" : "#ffffff",
          transition: reduce ? undefined : "fill 150ms ease-out, stroke 150ms ease-out",
        }}
      />
    </svg>
  )
}
